import {
	AutocompleteField,
	BeAppIds,
	COLORS,
	FeClientIds,
	IconButton,
	Icons,
	MaterialIcons,
	PrimaryButton,
	RegularButtonWrapper,
	SecondaryButton,
	TertiaryButton,
	TextInputField,
	platformService,
	AutocompleteOption,
} from '@monthio/react-components'
import React, {
	FunctionComponent,
	useCallback,
	useEffect,
	useState,
} from 'react'
import styled from 'styled-components'
import { localOverridesService } from '../localOverridesServices'
import { DevToolsPortal } from '../DevToolsPortal'

const suggestLocalAddress = (appId: BeAppIds | FeClientIds) => {
	switch (appId) {
		case 'budgetAdminClient':
			return 'http://localhost:3001'
		case 'budgetBusinessClient':
			return 'http://localhost:3002'
		case 'budgetConsumerClient':
			return 'http://localhost:3003'
		case 'eskatClient':
		case 'eskatClientFlow':
			return 'http://localhost:3004'
		case 'statisticsReportClient':
			return 'http://localhost:3005'
		case 'subscriptionClient':
			return 'http://localhost:3007'
		case 'transactionAdminClient':
			return 'http://localhost:3008'
		case 'transactionClient':
			return 'http://localhost:3009'
		case 'translationClient':
			return 'http://localhost:3010'
		case 'userManagementAdminClient':
			return 'http://localhost:3011'
		case 'userManagementClient':
			return 'http://localhost:3012'
		case 'smartcheckFlowClient':
			return 'http://localhost:3013'
		case 'auditClient':
			return 'http://localhost:3014'
	}
}

const Float = styled.div`
	position: fixed;
	top: 50%;
	left: 50%;
	-webkit-transform: translate(-50%, -50%);
	transform: translate(-50%, -50%);
	z-index: 1000;
`

const Wrapper = styled.div`
	width: 70vw;
	max-width: 950px;
	display: flex;
	flex-flow: column nowrap;
	background-color: ${COLORS.BACKGROUND_LIGHT};
	padding: 16px;
	border-radius: 6px;
	max-height: 70vh;
	overflow: auto;
	box-shadow:
		rgba(0, 0, 0, 0.1) 0px 1px 3px 0px,
		rgba(0, 0, 0, 0.06) 0px 1px 2px 0px;
`
const StyledTd = styled.td`
	min-width: 300px;
`

const Button = styled(TertiaryButton)`
	margin: 4px;

	> svg {
		padding-right: 0;
	}
`

const ButtonsBottom = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-top: 16px;
`

export const OverrideAppModal: FunctionComponent<{ children?: never }> = () => {
	const [showLocalOverride, setShowLocalOverride] = useState(false)
	return (
		<>
			<Button
				tooltip="Start a localoverride"
				tooltipPlacement="top"
				onClick={() => setShowLocalOverride((prev) => !prev)}
			>
				<MaterialIcons.PlayArrow />
				{showLocalOverride ? (
					<OverrideList
						startOverride={(apps) => {
							apps.forEach(({ id, url, checkUrl }) =>
								localOverridesService.enableOverride(id as any, url, checkUrl),
							)
							setShowLocalOverride(false)
						}}
					/>
				) : null}
			</Button>
			<Button
				tooltipPlacement="top"
				tooltip="Reset all overrides"
				onClick={localOverridesService.resetLocalOverrides}
			>
				<MaterialIcons.Stop />
			</Button>
		</>
	)
}

const overrideAppOptions = platformService.getConfigs().apps.map((app) => ({
	displayValue: `${app.name} (${app.id})`,
	value: app.id,
}))

const overrideClientOptions = platformService
	.getConfigs()
	.clients.map((client) => ({
		displayValue: `${client.name} (${client.id})`,
		value: client.id,
	}))

const findOption = (
	overrides: typeof overrideClientOptions,
	override: string,
) => {
	const result = overrides.find((o) => o.value === override)
	if (result == null) {
		return override
	}

	return result
}
const shiftInitialToBottom = (a: AppOverride, b: AppOverride) => {
	if (a.id === 'initial' || b.id === 'initial') {
		if (a.id === b.id) return 0
		return b.id === 'initial' ? -1 : 1
	}
	return 0
}
const filterOutInitial = (o: AppOverride) => o.id !== 'initial'

interface AppOverride {
	url: string
	id: string
	checkUrl?: string
	type: 'app' | 'client'
}

const clientFilter = (override: AppOverride) => override.type === 'client'
const appFilter = (override: AppOverride) => override.type === 'app'

const OverrideList: FunctionComponent<{
	startOverride: (overrides: AppOverride[]) => void
	children?: never
}> = ({ startOverride }) => {
	const [appOverrides, setAppOverrides] = useState<AppOverride[]>([
		{ id: 'initial', url: '', type: 'client' },
		{ id: 'initial', url: '', type: 'app' },
	])

	const handleChange = useCallback(
		(type: 'add' | 'delete', override: AppOverride) => {
			switch (type) {
				case 'delete':
					setAppOverrides((prev) =>
						prev.filter((item) => item.id !== override.id),
					)
					return
				case 'add':
					setAppOverrides((prev) => {
						const app = prev.find((t) => t.id === override.id)
						if (app == null) {
							return [...prev, override].sort(shiftInitialToBottom)
						}
						app.checkUrl = override.checkUrl
						app.url = override.url
						return [...prev]
					})
					return
			}
		},
		[],
	)

	const usedIds = appOverrides.map((a) => a.id)
	const filteredOverrides = appOverrides.filter(filterOutInitial)
	const disabled = filteredOverrides.some((a) => a.url.length < 2)

	return (
		<DevToolsPortal>
			<Float>
				<Wrapper>
					<table>
						<thead>
							<tr>
								<th>Client</th>
								<th>Override url</th>
								<th>Health path</th>
								<th />
							</tr>
						</thead>
						<tbody>
							{appOverrides.filter(clientFilter).map((t, i) => (
								<AppOverride
									updateApp={handleChange}
									usedIds={usedIds}
									type="client"
									key={t.id === 'initial' ? t.id + i : t.id}
									externalId={t.id}
								/>
							))}
						</tbody>
					</table>
					<table>
						<thead>
							<tr>
								<th>App</th>
								<th>Override url</th>
								<th>Health path</th>
								<th />
							</tr>
						</thead>
						<tbody>
							{appOverrides.filter(appFilter).map((t, i) => (
								<AppOverride
									updateApp={handleChange}
									usedIds={usedIds}
									key={t.id === 'initial' ? t.id + i : t.id}
									externalId={t.id}
									type="app"
								/>
							))}
						</tbody>
					</table>
					<ButtonsBottom>
						<RegularButtonWrapper>
							<SecondaryButton onClick={() => startOverride([])}>
								Cancel
							</SecondaryButton>
						</RegularButtonWrapper>
						<RegularButtonWrapper>
							<PrimaryButton
								disabled={disabled}
								tooltip={disabled ? 'Some apps are missing a url' : undefined}
								onClick={() => startOverride(filteredOverrides)}
							>
								Submit
							</PrimaryButton>
						</RegularButtonWrapper>
					</ButtonsBottom>
				</Wrapper>
			</Float>
		</DevToolsPortal>
	)
}

const AppOverride: FunctionComponent<{
	updateApp: (type: 'add' | 'delete', override: AppOverride) => void
	usedIds: string[]
	externalId?: string
	type: 'client' | 'app'
	children?: never
}> = ({ updateApp, usedIds, externalId, type }) => {
	const [url, setUrl] = useState<string | null>(null)
	const [id, setId] = useState<string | null>(externalId ?? null)
	const [checkPath, setCheckPath] = useState<string | undefined>()
	useEffect(() => {
		if (url == null && id != null) {
			setUrl(suggestLocalAddress(id as any) ?? '')
		}
	}, [id, url, setUrl])

	useEffect(() => {
		if (id != null) {
			updateApp('add', { id, url: url ?? '', checkUrl: checkPath, type })
		}
	}, [id, url, checkPath])
	const options = type === 'app' ? overrideAppOptions : overrideClientOptions

	return (
		<tr>
			<StyledTd>
				<AutocompleteField
					options={options.filter((t) => !usedIds.includes(t.value))}
					onChange={(_, option) => {
						const value = option as AutocompleteOption
						if (value?.value != null) {
							setId(String(value.value))
							return
						}
						setId((prev) => {
							if (prev != null) {
								updateApp('delete', { id: prev, url: url ?? '', type })
							}
							return null
						})
					}}
					value={
						id == null || id === 'initial'
							? null
							: (findOption(options, id) as any)
					}
					variant="outlined"
				/>
			</StyledTd>
			<StyledTd>
				<TextInputField
					value={url ?? ''}
					variant="outlined"
					onChange={(e) => {
						const value = e.target.value
						setUrl(value)
					}}
				/>
			</StyledTd>
			<td>
				<TextInputField
					variant="outlined"
					onChange={(e) => setCheckPath(e.target.value)}
				/>
			</td>
			<td>
				{id != null && id !== 'initial' ? (
					<IconButton
						onClick={() => {
							setId(null)
							updateApp('delete', { id, url: url ?? '', type })
						}}
					>
						<Icons.RemoveIcon />
					</IconButton>
				) : null}
			</td>
		</tr>
	)
}
